'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { BookOpen, Search, Plus, Edit2, Trash2, Eye, Tag, Clock, ChevronRight, Loader2, RefreshCw, ArrowLeft } from 'lucide-react';
import Link from 'next/link';

interface KbArticle {
  id: string;
  slug: string;
  title: string;
  summary: string;
  content: string;
  category: string;
  tags: string[];
  readTimeMinutes: number;
  views: number;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

const CATEGORIES = ['Tutti', 'Iniziare', 'Chiamate', 'Pazienti', 'Esami', 'Analytics', 'Integrazioni', 'Sicurezza', 'Fatturazione'];

export default function HelpKbPage() {
  const [articles, setArticles] = useState<KbArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Tutti');
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', summary: '', content: '', category: 'Iniziare', tags: '', published: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category !== 'Tutti') params.set('category', category);
      const res = await fetch(`/api/help/kb?${params}`);
      const json = await res.json();
      setArticles(json.data ?? json.articles ?? []);
    } finally {
      setLoading(false);
    }
  }, [search, category]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title) return;
    setSaving(true);
    try {
      const res = await fetch('/api/help/kb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) }),
      });
      const json = await res.json();
      setArticles(prev => [json.article ?? json, ...prev]);
      setForm({ title: '', summary: '', content: '', category: 'Iniziare', tags: '', published: true });
      setShowCreate(false);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id: string) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/help/kb/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) }),
      });
      const json = await res.json();
      setArticles(prev => prev.map(a => a.id === id ? (json.article ?? json) : a));
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Eliminare questo articolo?')) return;
    await fetch(`/api/help/kb/${id}`, { method: 'DELETE' });
    setArticles(prev => prev.filter(a => a.id !== id));
  };

  const startEdit = (article: KbArticle) => {
    setEditingId(article.id);
    setForm({ title: article.title, summary: article.summary, content: article.content, category: article.category, tags: article.tags.join(', '), published: article.published });
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        <div className="bg-white border-b border-border px-6 lg:px-8 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link href="/help" className="p-2 text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft size={18} />
              </Link>
              <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                <BookOpen size={18} className="text-blue-600" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-foreground">Knowledge Base</h1>
                <p className="text-xs text-muted-foreground">Articoli e guide per LabCallManager</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={load} className="p-2 border border-border rounded-lg hover:bg-muted transition-colors">
                <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              </button>
              <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                <Plus size={15} /> Nuovo Articolo
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 lg:px-8 py-6 max-w-5xl mx-auto space-y-6">
          {/* Search & filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-48">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca articoli..." className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map(c => (
                <button key={c} onClick={() => setCategory(c)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${category === c ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>{c}</button>
              ))}
            </div>
          </div>

          {/* Create form */}
          {showCreate && (
            <div className="bg-white rounded-2xl border border-border p-6">
              <h2 className="font-semibold text-foreground mb-4">Nuovo Articolo</h2>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-foreground mb-1.5">Titolo *</label>
                    <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">Categoria</label>
                    <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30">
                      {CATEGORIES.filter(c => c !== 'Tutti').map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1.5">Tag (separati da virgola)</label>
                    <input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} placeholder="twilio, chiamate, setup" className="w-full px-3 py-2.5 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-foreground mb-1.5">Sommario</label>
                    <input value={form.summary} onChange={e => setForm(p => ({ ...p, summary: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-foreground mb-1.5">Contenuto</label>
                    <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} rows={6} className="w-full px-3 py-2.5 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={handleCreate} disabled={saving || !form.title} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                    Pubblica Articolo
                  </button>
                  <button onClick={() => setShowCreate(false)} className="px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted transition-colors">Annulla</button>
                </div>
              </div>
            </div>
          )}

          {/* Articles list */}
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin h-8 w-8 text-primary" /></div>
          ) : (
            <div className="space-y-3">
              {articles.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-border">
                  <BookOpen size={32} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">Nessun articolo trovato</p>
                  <button onClick={() => setShowCreate(true)} className="mt-4 text-sm text-primary hover:underline">Crea il primo articolo</button>
                </div>
              ) : articles.map(article => (
                <div key={article.id} className="bg-white rounded-2xl border border-border overflow-hidden">
                  {editingId === article.id ? (
                    <div className="p-5 space-y-3">
                      <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 font-semibold" />
                      <input value={form.summary} onChange={e => setForm(p => ({ ...p, summary: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" placeholder="Sommario" />
                      <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} rows={4} className="w-full px-3 py-2 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                      <div className="flex gap-2">
                        <button onClick={() => handleUpdate(article.id)} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors">
                          {saving ? <Loader2 size={12} className="animate-spin" /> : <Edit2 size={12} />} Salva
                        </button>
                        <button onClick={() => setEditingId(null)} className="px-3 py-1.5 border border-border rounded-lg text-xs hover:bg-muted transition-colors">Annulla</button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full">{article.category}</span>
                            {!article.published && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">Bozza</span>}
                          </div>
                          <h3 className="font-semibold text-foreground mb-1">{article.title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">{article.summary}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Clock size={11} /> {article.readTimeMinutes} min</span>
                            <span className="flex items-center gap-1"><Eye size={11} /> {article.views} visualizzazioni</span>
                            {article.tags.map(tag => (
                              <span key={tag} className="flex items-center gap-1"><Tag size={10} /> {tag}</span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-4 shrink-0">
                          <Link href={`/help/kb/${article.slug}`} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors">
                            <Eye size={14} />
                          </Link>
                          <button onClick={() => startEdit(article)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => handleDelete(article.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                            <Trash2 size={14} />
                          </button>
                          <Link href={`/help/kb/${article.slug}`} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors">
                            <ChevronRight size={14} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
